import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import store from './store';
import PageNotAccessible from './modules/shared/pages/PageNotAccessible';
import { SidebarProvider } from './modules/shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import configureMixpanel from './modules/builder/utils/mixpanelConfig';
import SingleTabModal from './modules/shared/components/ui/SingleTabModal';

Sentry.init({
  dsn: 'https://77038894211c10612bc91128cc96d202@o4508657584832512.ingest.us.sentry.io/4508657587978240',
  integrations: [
    Sentry.browserTracingIntegration({
      tracePropagationTargets: [
        'localhost',
        'https://dev.housingram.com/api',
        'https://application.housingram.com/api'
      ]
    }),
    // Replace new Sentry.Replay() with the replayIntegration() function
    Sentry.replayIntegration()
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
});

mixpanel.init('4e3cbe78dfcd97bb4cac5afed63c4a54', {
  debug: false, // Reduced debug to minimize console spam
  track_pageview: true,
  persistence: 'localStorage',
  batch_requests: true,
  batch_size: 50,
  batch_flush_interval_ms: 5000, // Flush every 5 seconds to prevent mutex issues
  api_timeout_ms: 10000, // 10 seconds timeout
  verbose: false // Reduce console verbosity
});

// Apply additional configuration optimizations
configureMixpanel();
// Returns true if the current path matches /something/someNumber
const isPublicCampaignUrl = () => {
  const path = window.location.pathname;
  return /^\/[^/]+\/\d+$/.test(path);
};

// Returns true if the current path is a template page that should be mobile accessible
const isTemplateUrl = () => {
  const path = window.location.pathname;
  return /^\/[^/]+\/\d+(\/true|\/false)?$/.test(path);
};

const RootComponent = () => {
  const [isAccessible, setIsAccessible] = useState(window.innerWidth >= 1000 || isTemplateUrl());
  const [accessBlocked, setAccessBlocked] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsAccessible(window.innerWidth >= 1000 || isTemplateUrl());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent scroll on number inputs globally
  useEffect(() => {
    const preventScrollOnNumberInputs = (e) => {
      if (e.target.type === 'number') {
        e.preventDefault();
      }
    };

    // Add event listener to document
    document.addEventListener('wheel', preventScrollOnNumberInputs, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventScrollOnNumberInputs);
    };
  }, []);

  useEffect(() => {
    // Only apply single-tab logic if NOT a public campaign URL
    if (!isPublicCampaignUrl()) {
      const channel = new window.BroadcastChannel('');
      channel.postMessage('Tab Opened');
      channel.onmessage = (event) => {
        if (event.data === 'Tab Opened') channel.postMessage('Block');
        else if (event.data === 'Block') {
          channel.close();
          setAccessBlocked(true);
        }
      };
      return () => channel.close();
    }
  }, []);

  return isAccessible && !accessBlocked ? (
    <Provider store={store}>
      <SidebarProvider>
        <App />
      </SidebarProvider>
    </Provider>
  ) : !accessBlocked ? (
    <PageNotAccessible />
  ) : (
    <SingleTabModal />
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<RootComponent />);
